// Cosmic backdrop — layered starfield + nebula cloud + galaxy dust.
// One build, added to the scene once, spans the whole session.

import {
  AdditiveBlending,
  BackSide,
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Mesh,
  NormalBlending,
  Points,
  RingGeometry,
  ShaderMaterial,
  SphereGeometry,
} from "three";
import { currentTheme } from "../theme";
import type { BiomeTheme } from "@shared/biome";

// Biome ids that get the Saturn-style ringed planet (the dreamy palettes).
const DREAMY_BIOMES = new Set(["celestial", "golden", "aurora"]);

export interface Backdrop {
  root: Group;
  update(t: number): void;
  /** Pull the current (active) biome's nebula palette into the shader
   *  uniforms. Called between holes so the sky shifts without having to
   *  rebuild the whole 1500-unit skydome + star layers. */
  applyTheme(): void;
  dispose(): void;
}

export function buildBackdrop(isMobile: boolean): Backdrop {
  const root = new Group();
  const disposables: { dispose(): void }[] = [];
  const theme = currentTheme();

  // Far skydome with a procedural nebula shader. Renders on the inside of a
  // giant sphere so it always sits behind everything. Colors come from the
  // daily biome theme — each course day paints the sky differently.
  const nebulaGeom = new SphereGeometry(1500, 48, 32);
  const nebulaMat = new ShaderMaterial({
    side: BackSide,
    depthWrite: false,
    uniforms: {
      uTime: { value: 0 },
      uColorA: { value: new Color(...theme.nebulaA) },
      uColorB: { value: new Color(...theme.nebulaB) },
      uColorC: { value: new Color(...theme.nebulaC) },
      uColorD: { value: new Color(...theme.nebulaD) },
    },
    vertexShader: /* glsl */ `
      varying vec3 vPos;
      void main() {
        vPos = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vPos;
      uniform float uTime;
      uniform vec3 uColorA;
      uniform vec3 uColorB;
      uniform vec3 uColorC;
      uniform vec3 uColorD;

      // 3D hash + simplex-ish noise (cheap, tileable-enough for a backdrop)
      vec3 hash3(vec3 p) {
        p = vec3(
          dot(p, vec3(127.1, 311.7, 74.7)),
          dot(p, vec3(269.5, 183.3, 246.1)),
          dot(p, vec3(113.5, 271.9, 124.6))
        );
        return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
      }
      float noise(vec3 p) {
        vec3 i = floor(p);
        vec3 f = fract(p);
        vec3 u = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(
            mix(dot(hash3(i + vec3(0,0,0)), f - vec3(0,0,0)),
                dot(hash3(i + vec3(1,0,0)), f - vec3(1,0,0)), u.x),
            mix(dot(hash3(i + vec3(0,1,0)), f - vec3(0,1,0)),
                dot(hash3(i + vec3(1,1,0)), f - vec3(1,1,0)), u.x), u.y),
          mix(
            mix(dot(hash3(i + vec3(0,0,1)), f - vec3(0,0,1)),
                dot(hash3(i + vec3(1,0,1)), f - vec3(1,0,1)), u.x),
            mix(dot(hash3(i + vec3(0,1,1)), f - vec3(0,1,1)),
                dot(hash3(i + vec3(1,1,1)), f - vec3(1,1,1)), u.x), u.y),
          u.z);
      }
      float fbm(vec3 p) {
        float v = 0.0;
        float a = 0.5;
        for (int i = 0; i < 5; i++) {
          v += a * noise(p);
          p *= 2.02;
          a *= 0.5;
        }
        return v;
      }

      void main() {
        vec3 p = vPos * 2.2;
        float slow = uTime * 0.015;
        float n1 = fbm(p + vec3(slow, 0.0, -slow));
        float n2 = fbm(p * 1.7 - vec3(0.0, slow * 1.3, 0.0));
        float cloud = smoothstep(0.1, 0.7, n1 + 0.3 * n2);
        float hot = smoothstep(0.55, 0.95, n1 * 1.3);
        vec3 col = mix(uColorD, uColorA, cloud);
        col = mix(col, uColorB, smoothstep(0.3, 0.8, n2));
        col = mix(col, uColorC, hot * 0.75);
        // Slight vertical gradient so the dome feels oriented.
        col *= 0.85 + 0.15 * (0.5 + 0.5 * vPos.y);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  const nebula = new Mesh(nebulaGeom, nebulaMat);
  root.add(nebula);
  disposables.push(nebulaGeom, nebulaMat);

  // Distant star shell — tiny points at high radius, slight twinkle.
  const farStarCount = isMobile ? 2500 : 6000;
  const farStars = buildStarLayer({
    count: farStarCount,
    radiusMin: 900,
    radiusMax: 1200,
    sizeMin: 0.7,
    sizeMax: 1.4,
    palette: theme.starPalette,
    opacity: 0.95,
  });
  root.add(farStars.points);
  disposables.push(farStars);

  // Mid-range bright stars — fewer but bigger, gives parallax.
  const midStarCount = isMobile ? 300 : 700;
  const midStars = buildStarLayer({
    count: midStarCount,
    radiusMin: 450,
    radiusMax: 700,
    sizeMin: 1.5,
    sizeMax: 3.2,
    palette: theme.starPalette.slice(0, 3),
    opacity: 1.0,
  });
  root.add(midStars.points);
  disposables.push(midStars);

  // Galactic dust cloud — giant stretched sphere of soft points oriented
  // like a milky-way band. Palette comes from the theme.
  const dust = buildDustBand(
    isMobile ? 600 : 1400,
    theme.dustPalette[0],
    theme.dustPalette[1],
    theme.dustPalette[2],
  );
  root.add(dust.points);
  disposables.push(dust);

  // Cloud patches — closer-in "puffy" billboard points that sell softness.
  // Only meaningful for the celestial/dreamy biomes; starlight stays sharp.
  const cloudCount =
    theme.id === "celestial"
      ? isMobile ? 40 : 80
      : theme.id === "golden" || theme.id === "aurora"
        ? isMobile ? 20 : 40
        : 0;
  if (cloudCount > 0) {
    const clouds = buildCloudPatches(
      cloudCount,
      theme.dustPalette[0],
      theme.dustPalette[2],
    );
    root.add(clouds.points);
    disposables.push(clouds);
  }

  // ---------- Decorative parallax planets ----------
  // Solid bodies in the upper hemisphere at far distance — they catch the
  // virtual sun, tint from the biome's palette, and rotate slowly. Not gas:
  // NormalBlending so bloom doesn't blow them out on top of the reduced
  // strength (0.42 desktop / 0.3 mobile).
  const planetCount = isMobile ? 2 : 3 + Math.floor(Math.random() * 3); // 2 mobile, 3-5 desktop
  const planets = buildDecorPlanets(planetCount, theme);
  root.add(planets.group);
  disposables.push(planets);

  // ---------- Saturn-style ringed planet (dreamy biomes, desktop only) ----------
  // Rings are the priciest piece (alpha shader + two meshes) so mobile skips
  // entirely. Built once, toggled via `visible` when the biome rotates.
  const ringed = isMobile
    ? null
    : buildRingedPlanet(theme);
  if (ringed) {
    ringed.group.visible = DREAMY_BIOMES.has(theme.id);
    root.add(ringed.group);
    disposables.push(ringed);
  }

  // ---------- Drifting moonlets / orbs ----------
  // Easter-egg "floating golf balls" from the reference image — small soft
  // orbs at mid-distance, each on its own slow orbit.
  const moonletCount = isMobile ? 4 : 6 + Math.floor(Math.random() * 5); // 4 mobile, 6-10 desktop
  const moonlets = buildMoonlets(moonletCount, theme);
  root.add(moonlets.group);
  disposables.push(moonlets);

  return {
    root,
    update(t: number) {
      (nebulaMat.uniforms.uTime as { value: number }).value = t;
      // Stars are STATIC. Any rotation at distance causes sub-pixel motion
      // that bloom amplifies into flicker. Only the nebula shader moves.
      planets.update(t);
      ringed?.update(t);
      moonlets.update(t);
    },
    applyTheme() {
      const t = currentTheme();
      (nebulaMat.uniforms.uColorA.value as Color).setRGB(...t.nebulaA);
      (nebulaMat.uniforms.uColorB.value as Color).setRGB(...t.nebulaB);
      (nebulaMat.uniforms.uColorC.value as Color).setRGB(...t.nebulaC);
      (nebulaMat.uniforms.uColorD.value as Color).setRGB(...t.nebulaD);
      planets.applyTheme(t);
      if (ringed) {
        ringed.applyTheme(t);
        ringed.group.visible = DREAMY_BIOMES.has(t.id);
      }
      moonlets.applyTheme(t);
    },
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

interface StarLayer {
  points: Points;
  dispose(): void;
}

function buildStarLayer(opts: {
  count: number;
  radiusMin: number;
  radiusMax: number;
  sizeMin: number;
  sizeMax: number;
  palette: string[];
  opacity: number;
}): StarLayer {
  const { count, radiusMin, radiusMax, sizeMin, sizeMax, palette, opacity } =
    opts;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const paletteColors = palette.map((c) => new Color(c));
  for (let i = 0; i < count; i++) {
    const r = radiusMin + Math.random() * (radiusMax - radiusMin);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi);
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    const c = paletteColors[Math.floor(Math.random() * paletteColors.length)]!;
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
    sizes[i] = sizeMin + Math.random() * (sizeMax - sizeMin);
  }
  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  geom.setAttribute("size", new Float32BufferAttribute(sizes, 1));

  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: {
      uOpacity: { value: opacity },
    },
    vertexShader: /* glsl */ `
      attribute float size;
      varying vec3 vColor;
      varying float vSize;
      void main() {
        vColor = color;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        // Hard-clamp point size to at least 2 pixels so far stars never flicker
        // between sub-pixel sampling frames (that's what made them twinkle like
        // Christmas lights).
        float px = size * (300.0 / -mv.z);
        vSize = px;
        gl_PointSize = max(2.0, px);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vColor;
      varying float vSize;
      uniform float uOpacity;
      void main() {
        vec2 d = gl_PointCoord - vec2(0.5);
        float r = length(d);
        // Soft disc — but scale alpha down when the star was upsized from
        // sub-pixel to the 2-pixel floor so tiny far stars stay subtle.
        float core = smoothstep(0.5, 0.25, r);
        float floorAdjust = smoothstep(2.0, 0.6, vSize);
        float a = core * uOpacity * (1.0 - 0.55 * floorAdjust);
        if (a < 0.01) discard;
        gl_FragColor = vec4(vColor, a);
      }
    `,
    vertexColors: true,
  });
  const points = new Points(geom, mat);
  return {
    points,
    dispose() {
      geom.dispose();
      mat.dispose();
    },
  };
}

// Large puffy cloud patches rendered as Points with a fbm-alpha disc shader.
// Cheap way to fake volumetric clouds without a raymarch.
function buildCloudPatches(
  count: number,
  warmHex: string,
  hotHex: string,
): StarLayer {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const seeds = new Float32Array(count);
  const warm = new Color(warmHex);
  const hot = new Color(hotHex);
  for (let i = 0; i < count; i++) {
    // Distribute on a flattened upper hemisphere around the playfield.
    const theta = Math.random() * Math.PI * 2;
    const r = 450 + Math.random() * 280;
    const y = 40 + Math.random() * 260;
    positions[i * 3] = Math.cos(theta) * r;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = Math.sin(theta) * r;
    const mix = Math.random();
    const col = warm.clone().lerp(hot, mix);
    colors[i * 3] = col.r;
    colors[i * 3 + 1] = col.g;
    colors[i * 3 + 2] = col.b;
    sizes[i] = 180 + Math.random() * 260;
    seeds[i] = Math.random() * 100;
  }
  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  geom.setAttribute("size", new Float32BufferAttribute(sizes, 1));
  geom.setAttribute("seed", new Float32BufferAttribute(seeds, 1));
  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    vertexColors: true,
    blending: AdditiveBlending,
    uniforms: {},
    vertexShader: /* glsl */ `
      attribute float size;
      attribute float seed;
      varying vec3 vColor;
      varying float vSeed;
      void main() {
        vColor = color;
        vSeed = seed;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        // Perspective-aware size so clouds near camera puff wider.
        gl_PointSize = size * (300.0 / -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vColor;
      varying float vSeed;
      // Cheap 2D value noise.
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(hash(i), hash(i + vec2(1,0)), u.x),
          mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x),
          u.y);
      }
      float fbm(vec2 p) {
        float v = 0.0;
        float a = 0.55;
        for (int i = 0; i < 4; i++) {
          v += a * noise(p);
          p *= 2.02;
          a *= 0.55;
        }
        return v;
      }
      void main() {
        vec2 d = gl_PointCoord - vec2(0.5);
        float r = length(d);
        // Soft disc mask.
        float disc = smoothstep(0.5, 0.05, r);
        // Noise texture — offset by seed so each cloud has its own shape.
        float n = fbm((gl_PointCoord + vec2(vSeed)) * 3.5);
        float alpha = disc * smoothstep(0.15, 0.65, n) * 0.14;
        if (alpha < 0.004) discard;
        gl_FragColor = vec4(vColor, alpha);
      }
    `,
  });
  const points = new Points(geom, mat);
  points.frustumCulled = false;
  return {
    points,
    dispose() {
      geom.dispose();
      mat.dispose();
    },
  };
}

// A diagonal band of softer points, evoking galactic dust.
function buildDustBand(
  count: number,
  warmHex: string,
  coolHex: string,
  hotHex: string,
): StarLayer {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const warm = new Color(warmHex);
  const cool = new Color(coolHex);
  const hot = new Color(hotHex);
  for (let i = 0; i < count; i++) {
    // Sample inside a stretched ellipsoid oriented along (1, 0.15, 0.9).
    const r = 520 + Math.random() * 280;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.cos(phi) * 0.18; // flatten
    const z = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    const mix = Math.random();
    const col =
      mix < 0.4
        ? warm.clone().lerp(hot, Math.random() * 0.6)
        : warm.clone().lerp(cool, Math.random() * 0.8);
    colors[i * 3] = col.r;
    colors[i * 3 + 1] = col.g;
    colors[i * 3 + 2] = col.b;
    sizes[i] = 4 + Math.random() * 10;
  }
  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  geom.setAttribute("size", new Float32BufferAttribute(sizes, 1));
  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    vertexColors: true,
    uniforms: {},
    vertexShader: /* glsl */ `
      attribute float size;
      varying vec3 vColor;
      void main() {
        vColor = color;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = max(3.0, size * (400.0 / -mv.z));
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vColor;
      void main() {
        vec2 d = gl_PointCoord - vec2(0.5);
        float r = length(d);
        float a = smoothstep(0.5, 0.0, r) * 0.18;
        if (a < 0.005) discard;
        gl_FragColor = vec4(vColor, a);
      }
    `,
  });
  const points = new Points(geom, mat);
  return {
    points,
    dispose() {
      geom.dispose();
      mat.dispose();
    },
  };
}

// ============================================================================
// Decorative backdrop planets (parallax, Ghibli-poster vibe)
// ============================================================================

// Cheap Lambert + rim shader. Because the scene has no global lights, we bake
// the virtual sun direction straight into the material so these bodies catch
// the biome's virtual sun without a DirectionalLight per planet.
const PLANET_VERT = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

const PLANET_FRAG = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  uniform vec3 uColor;
  uniform vec3 uRimColor;
  uniform vec3 uSunDirView;
  uniform float uAmbient;

  void main() {
    vec3 n = normalize(vNormal);
    float lambert = max(0.0, dot(n, normalize(uSunDirView)));
    // Terminator soften + warm ambient shadow side so planets never go pitch black.
    float shade = uAmbient + (1.0 - uAmbient) * smoothstep(0.0, 0.35, lambert);
    vec3 body = uColor * shade;
    // Rim light along the silhouette, biased by sun so it looks lit-from-behind.
    float rim = pow(1.0 - max(0.0, dot(n, vViewDir)), 2.5);
    rim *= 0.55 + 0.45 * smoothstep(-0.2, 0.6, dot(n, normalize(uSunDirView)));
    vec3 col = body + uRimColor * rim * 0.55;
    gl_FragColor = vec4(col, 1.0);
  }
`;

interface DecorPlanets {
  group: Group;
  update(t: number): void;
  applyTheme(theme: BiomeTheme): void;
  dispose(): void;
}

function buildDecorPlanets(count: number, theme: BiomeTheme): DecorPlanets {
  const group = new Group();
  // Share one geometry across all planets — shader material keeps per-planet
  // tint & rim in uniforms.
  const geom = new SphereGeometry(1, 24, 16);
  const mats: ShaderMaterial[] = [];
  const meshes: {
    mesh: Mesh;
    axis: [number, number, number];
    omega: number;
  }[] = [];

  // Spread planets around the upper hemisphere at evenly spaced azimuths so
  // they don't clump. Each gets a random radius + size + y within a band.
  for (let i = 0; i < count; i++) {
    const azimuth = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const radius = 600 + Math.random() * 500; // 600-1100
    const y = 180 + Math.random() * 380;
    const size = 30 + Math.random() * 70; // 30-100
    const baseColor = tintForIndex(theme, i, count);
    const rimColor = new Color(theme.starPalette[0] ?? "#ffffff");
    const mat = new ShaderMaterial({
      transparent: false,
      blending: NormalBlending,
      uniforms: {
        uColor: { value: baseColor },
        uRimColor: { value: rimColor },
        uSunDirView: { value: sunDirVec(theme) },
        uAmbient: { value: 0.22 },
      },
      vertexShader: PLANET_VERT,
      fragmentShader: PLANET_FRAG,
    });
    const mesh = new Mesh(geom, mat);
    mesh.position.set(
      Math.cos(azimuth) * radius,
      y,
      Math.sin(azimuth) * radius,
    );
    mesh.scale.setScalar(size);
    // Far distance; keep them drawn even when camera frustum is clipped.
    mesh.frustumCulled = false;
    // Random tilted spin axis + slow rate.
    const axis: [number, number, number] = [
      Math.random() * 2 - 1,
      Math.random() * 2 - 1,
      Math.random() * 2 - 1,
    ];
    const al = Math.hypot(axis[0], axis[1], axis[2]) || 1;
    axis[0] /= al; axis[1] /= al; axis[2] /= al;
    const omega = 0.002 + Math.random() * 0.008;
    group.add(mesh);
    mats.push(mat);
    meshes.push({ mesh, axis, omega });
  }

  return {
    group,
    update(t: number) {
      for (const p of meshes) {
        p.mesh.rotation.set(
          p.axis[0] * p.omega * t,
          p.axis[1] * p.omega * t,
          p.axis[2] * p.omega * t,
        );
      }
    },
    applyTheme(next: BiomeTheme) {
      const sunView = sunDirVec(next);
      for (let i = 0; i < mats.length; i++) {
        const m = mats[i]!;
        (m.uniforms.uColor.value as Color).copy(tintForIndex(next, i, mats.length));
        (m.uniforms.uRimColor.value as Color).set(next.starPalette[0] ?? "#ffffff");
        const sd = m.uniforms.uSunDirView.value as [number, number, number];
        sd[0] = sunView[0]; sd[1] = sunView[1]; sd[2] = sunView[2];
      }
    },
    dispose() {
      geom.dispose();
      for (const m of mats) m.dispose();
    },
  };
}

// Pick a base color per planet index, mixing from the biome's dust + nebula
// palettes so color shifts are legible when the biome rotates.
function tintForIndex(theme: BiomeTheme, i: number, n: number): Color {
  const dust = theme.dustPalette;
  const base = new Color(dust[i % dust.length]!);
  const nebC = new Color(...theme.nebulaC);
  const mix = 0.25 + 0.15 * Math.sin((i / Math.max(1, n)) * Math.PI * 2);
  return base.lerp(nebC, mix);
}

// Virtual sun direction encoded for the planet shader. Returning a plain
// tuple — cheaper to swap in `applyTheme` than replacing a Vector3 uniform.
function sunDirVec(theme: BiomeTheme): [number, number, number] {
  const [x, y, z] = theme.sunDir;
  const l = Math.hypot(x, y, z) || 1;
  return [x / l, y / l, z / l];
}

// ============================================================================
// Saturn-style ringed planet (dreamy biomes only)
// ============================================================================

interface RingedPlanet {
  group: Group;
  update(t: number): void;
  applyTheme(theme: BiomeTheme): void;
  dispose(): void;
}

function buildRingedPlanet(theme: BiomeTheme): RingedPlanet {
  const group = new Group();
  // Fixed placement — looks intentional vs. random-every-boot.
  const azimuth = 1.8;
  const radius = 820;
  const y = 240;
  group.position.set(
    Math.cos(azimuth) * radius,
    y,
    Math.sin(azimuth) * radius,
  );
  const bodyRadius = 95;

  // Body — reuse the same planet shader as decor planets.
  const bodyGeom = new SphereGeometry(1, 32, 24);
  const bodyMat = new ShaderMaterial({
    transparent: false,
    blending: NormalBlending,
    uniforms: {
      uColor: {
        value: new Color(theme.dustPalette[0]!).lerp(new Color(...theme.nebulaC), 0.35),
      },
      uRimColor: { value: new Color(theme.starPalette[0] ?? "#ffffff") },
      uSunDirView: { value: sunDirVec(theme) },
      uAmbient: { value: 0.25 },
    },
    vertexShader: PLANET_VERT,
    fragmentShader: PLANET_FRAG,
  });
  const body = new Mesh(bodyGeom, bodyMat);
  body.scale.setScalar(bodyRadius);
  body.frustumCulled = false;
  group.add(body);

  // Ring — flat annulus, tilted, with a transparent shader that fades radially
  // and carves out a thin gap so it reads as proper Saturn rings rather than
  // a solid pancake.
  const innerR = bodyRadius * 1.5;
  const outerR = bodyRadius * 2.6;
  const ringGeom = new RingGeometry(innerR, outerR, 128, 1);
  const ringMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: NormalBlending,
    side: DoubleSide,
    uniforms: {
      uColor: { value: new Color(theme.dustPalette[2]!) },
      uInnerR: { value: innerR },
      uOuterR: { value: outerR },
    },
    vertexShader: /* glsl */ `
      varying vec2 vLocal;
      void main() {
        vLocal = position.xy;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec2 vLocal;
      uniform vec3 uColor;
      uniform float uInnerR;
      uniform float uOuterR;
      void main() {
        float r = length(vLocal);
        // Normalized position along the ring [0..1], 0 = inner, 1 = outer.
        float u = clamp((r - uInnerR) / (uOuterR - uInnerR), 0.0, 1.0);
        // Sharp inner edge, soft outer edge.
        float innerFade = smoothstep(0.0, 0.05, u);
        float outerFade = 1.0 - smoothstep(0.6, 1.0, u);
        // Cassini-like gap band roughly a third of the way out.
        float gapCenter = 0.42;
        float gap = 1.0 - (1.0 - smoothstep(gapCenter - 0.05, gapCenter, u)) * smoothstep(gapCenter, gapCenter + 0.05, u);
        float a = innerFade * outerFade * gap * 0.75;
        if (a < 0.01) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });
  const ring = new Mesh(ringGeom, ringMat);
  // Tilt the ring on X (plus tiny Z wobble so it doesn't feel axis-aligned).
  ring.rotation.x = -Math.PI * 0.5 + (18 * Math.PI) / 180;
  ring.rotation.z = (7 * Math.PI) / 180;
  ring.frustumCulled = false;
  group.add(ring);

  const omega = 0.0025;

  return {
    group,
    update(t: number) {
      body.rotation.y = omega * t;
    },
    applyTheme(next: BiomeTheme) {
      (bodyMat.uniforms.uColor.value as Color)
        .copy(new Color(next.dustPalette[0]!))
        .lerp(new Color(...next.nebulaC), 0.35);
      (bodyMat.uniforms.uRimColor.value as Color).set(next.starPalette[0] ?? "#ffffff");
      const sv = sunDirVec(next);
      const sd = bodyMat.uniforms.uSunDirView.value as [number, number, number];
      sd[0] = sv[0]; sd[1] = sv[1]; sd[2] = sv[2];
      (ringMat.uniforms.uColor.value as Color).set(next.dustPalette[2]!);
    },
    dispose() {
      bodyGeom.dispose();
      bodyMat.dispose();
      ringGeom.dispose();
      ringMat.dispose();
    },
  };
}

// ============================================================================
// Drifting moonlets — the "floating golf ball" easter egg
// ============================================================================

interface Moonlets {
  group: Group;
  update(t: number): void;
  applyTheme(theme: BiomeTheme): void;
  dispose(): void;
}

function buildMoonlets(count: number, theme: BiomeTheme): Moonlets {
  const group = new Group();
  // Shared geometry — per-mesh material so each orb has its own color.
  const geom = new SphereGeometry(1, 16, 12);
  const mats: ShaderMaterial[] = [];
  const items: {
    mesh: Mesh;
    angle0: number;
    radius: number;
    omega: number;
    y: number;
  }[] = [];

  for (let i = 0; i < count; i++) {
    const radius = 400 + Math.random() * 300;
    const angle0 = (i / count) * Math.PI * 2 + Math.random() * 0.8;
    const omega = (Math.random() < 0.5 ? 1 : -1) * (0.01 + Math.random() * 0.02);
    const y = 30 + Math.random() * 180;
    const size = 5 + Math.random() * 10;
    const color = new Color(theme.starPalette[i % theme.starPalette.length]!);
    // Roughly every third orb is a "golf ball" with faint dimples. The rest
    // are soft glowing pearls.
    const dimples = i % 3 === 0 ? 1 : 0;
    const mat = new ShaderMaterial({
      transparent: false,
      blending: NormalBlending,
      uniforms: {
        uColor: { value: color },
        uSunDirView: { value: sunDirVec(theme) },
        uDimples: { value: dimples },
      },
      vertexShader: /* glsl */ `
        varying vec3 vNormal;
        varying vec3 vLocal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vLocal = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vNormal;
        varying vec3 vLocal;
        uniform vec3 uColor;
        uniform vec3 uSunDirView;
        uniform float uDimples;

        float hash(vec3 p) {
          return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
        }

        void main() {
          vec3 n = normalize(vNormal);
          float lambert = max(0.0, dot(n, normalize(uSunDirView)));
          float shade = 0.35 + 0.65 * lambert;
          // Dimples: tile the unit sphere with a low-freq hash and darken
          // lightly where cell value is low. Stays subtle — no HDR punch.
          float d = 1.0;
          if (uDimples > 0.5) {
            vec3 cell = floor(vLocal * 6.0);
            float h = hash(cell);
            d = 1.0 - 0.18 * smoothstep(0.0, 0.35, 1.0 - h);
          }
          vec3 col = uColor * shade * d;
          // Gentle rim so they stand off the nebula.
          float rim = pow(1.0 - max(0.0, n.z), 2.5);
          col += uColor * rim * 0.3;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const mesh = new Mesh(geom, mat);
    mesh.scale.setScalar(size);
    mesh.frustumCulled = false;
    group.add(mesh);
    mats.push(mat);
    items.push({ mesh, angle0, radius, omega, y });
  }

  return {
    group,
    update(t: number) {
      for (const it of items) {
        const a = it.angle0 + it.omega * t;
        it.mesh.position.set(
          Math.cos(a) * it.radius,
          it.y,
          Math.sin(a) * it.radius,
        );
        // Subtle spin so dimples don't sit dead-on.
        it.mesh.rotation.y = a * 0.6;
      }
    },
    applyTheme(next: BiomeTheme) {
      const sv = sunDirVec(next);
      for (let i = 0; i < mats.length; i++) {
        const m = mats[i]!;
        (m.uniforms.uColor.value as Color).set(
          next.starPalette[i % next.starPalette.length]!,
        );
        const sd = m.uniforms.uSunDirView.value as [number, number, number];
        sd[0] = sv[0]; sd[1] = sv[1]; sd[2] = sv[2];
      }
    },
    dispose() {
      geom.dispose();
      for (const m of mats) m.dispose();
    },
  };
}
